import axios, { resolveActiveHospitalId } from "../utils/index";
import type {
  HospitalDoctorPreCredentialDTO,
  HospitalPreCredentialCreateRequestDTO,
  HospitalPreCredentialDocRequestPayload,
  HospitalPreCredentialRequestedFileDTO,
  HospitalPreCredentialSettingsDTO,
  HospitalPreCredentialUpdateRequestDTO,
  ResponseData,
  UpdatePreCredentialSettingsRequest,
} from "../interface/precredential";

const PRE_CREDENTIAL_PRIMARY_BASE = "/v1/hospital/precredentials";
const PRE_CREDENTIAL_LEGACY_BASE = "/admin/v1/hospital/precredentials";

const isSuccessCode = (code?: number) => code === 0 || code === 200;

const unwrapResponse = <T>(response: ResponseData<T>): T => {
  if (!isSuccessCode(response?.code)) {
    throw new Error(response?.message || response?.msg || "Request failed");
  }
  return response.data;
};

const resolveRequestAccountId = (accountId?: string): string | undefined => {
  const explicit = String(accountId || "").trim();
  if (explicit) return explicit;
  return resolveActiveHospitalId();
};

const withAccountHeader = (accountId?: string) => {
  const resolved = resolveRequestAccountId(accountId);
  if (!resolved) return undefined;
  return {
    headers: {
      accountId: resolved,
    },
  };
};

const shouldFallbackToLegacy = (error: any): boolean => {
  const status = Number(error?.response?.status || 0);
  return status === 404 || status === 405;
};

const withLegacyFallback = async <T>(
  primaryCall: () => Promise<ResponseData<T>>,
  legacyCall: () => Promise<ResponseData<T>>,
): Promise<T> => {
  try {
    return unwrapResponse(await primaryCall());
  } catch (error: any) {
    if (!shouldFallbackToLegacy(error)) {
      throw error;
    }
    return unwrapResponse(await legacyCall());
  }
};

export const getPreCredentialSettings = async (
  accountId?: string,
): Promise<HospitalPreCredentialSettingsDTO> => {
  return withLegacyFallback(
    async () =>
      (
        await axios.get<ResponseData<HospitalPreCredentialSettingsDTO>>(
          `${PRE_CREDENTIAL_PRIMARY_BASE}/settings`,
          withAccountHeader(accountId),
        )
      ).data,
    async () =>
      (
        await axios.get<ResponseData<HospitalPreCredentialSettingsDTO>>(
          `${PRE_CREDENTIAL_LEGACY_BASE}/settings`,
          withAccountHeader(accountId),
        )
      ).data,
  );
};

export const updatePreCredentialSettings = async (
  payload: UpdatePreCredentialSettingsRequest,
  accountId?: string,
): Promise<HospitalPreCredentialSettingsDTO> => {
  return withLegacyFallback(
    async () =>
      (
        await axios.put<ResponseData<HospitalPreCredentialSettingsDTO>>(
          `${PRE_CREDENTIAL_PRIMARY_BASE}/settings`,
          payload,
          withAccountHeader(accountId),
        )
      ).data,
    async () =>
      (
        await axios.put<ResponseData<HospitalPreCredentialSettingsDTO>>(
          `${PRE_CREDENTIAL_LEGACY_BASE}/settings`,
          payload,
          withAccountHeader(accountId),
        )
      ).data,
  );
};

export const createPreCredential = async (
  payload: HospitalPreCredentialCreateRequestDTO,
  accountId?: string,
): Promise<HospitalDoctorPreCredentialDTO> => {
  return withLegacyFallback(
    async () =>
      (
        await axios.post<ResponseData<HospitalDoctorPreCredentialDTO>>(
          PRE_CREDENTIAL_PRIMARY_BASE,
          payload,
          withAccountHeader(accountId),
        )
      ).data,
    async () =>
      (
        await axios.post<ResponseData<HospitalDoctorPreCredentialDTO>>(
          PRE_CREDENTIAL_LEGACY_BASE,
          payload,
          withAccountHeader(accountId),
        )
      ).data,
  );
};

export const updatePreCredential = async (
  preCredentialId: string,
  payload: HospitalPreCredentialUpdateRequestDTO,
  accountId?: string,
): Promise<HospitalDoctorPreCredentialDTO> => {
  return withLegacyFallback(
    async () =>
      (
        await axios.put<ResponseData<HospitalDoctorPreCredentialDTO>>(
          `${PRE_CREDENTIAL_PRIMARY_BASE}/${preCredentialId}`,
          payload,
          withAccountHeader(accountId),
        )
      ).data,
    async () =>
      (
        await axios.put<ResponseData<HospitalDoctorPreCredentialDTO>>(
          `${PRE_CREDENTIAL_LEGACY_BASE}/${preCredentialId}`,
          payload,
          withAccountHeader(accountId),
        )
      ).data,
  );
};

export const listPreCredentials = async (
  accountId?: string,
): Promise<HospitalDoctorPreCredentialDTO[]> => {
  return withLegacyFallback(
    async () =>
      (
        await axios.get<ResponseData<HospitalDoctorPreCredentialDTO[]>>(
          PRE_CREDENTIAL_PRIMARY_BASE,
          withAccountHeader(accountId),
        )
      ).data,
    async () =>
      (
        await axios.get<ResponseData<HospitalDoctorPreCredentialDTO[]>>(
          PRE_CREDENTIAL_LEGACY_BASE,
          withAccountHeader(accountId),
        )
      ).data,
  );
};

const PRE_CREDENTIAL_LIST_CACHE_MS = 60_000;
let preCredentialListCache: {
  hospitalId: string;
  rows: HospitalDoctorPreCredentialDTO[];
  fetchedAt: number;
} | null = null;

export function invalidatePreCredentialListCache(): void {
  preCredentialListCache = null;
}

export async function listPreCredentialsCached(
  accountId?: string,
): Promise<HospitalDoctorPreCredentialDTO[]> {
  const hospitalId = String(resolveRequestAccountId(accountId) || "").trim();
  const now = Date.now();
  if (
    hospitalId &&
    preCredentialListCache &&
    preCredentialListCache.hospitalId === hospitalId &&
    now - preCredentialListCache.fetchedAt < PRE_CREDENTIAL_LIST_CACHE_MS
  ) {
    return preCredentialListCache.rows;
  }
  if (!hospitalId) {
    return [];
  }
  const response = await listPreCredentials(hospitalId);
  const rows = Array.isArray(response) ? response : [];
  preCredentialListCache = { hospitalId, rows, fetchedAt: now };
  return rows;
}

export function findActivePreCredentialForDoctor(
  rows: HospitalDoctorPreCredentialDTO[],
  doctorId: string,
): HospitalDoctorPreCredentialDTO | null {
  const targetId = String(doctorId || "").trim();
  if (!targetId) return null;

  for (const row of rows) {
    if (String(row?.status || "").toUpperCase() !== "ACTIVE") {
      continue;
    }

    const directDoctorId = String(row?.doctorId || "").trim();
    if (directDoctorId && directDoctorId === targetId) {
      return row;
    }

    const snapshotRaw = String(row?.snapshotJson || "").trim();
    if (!snapshotRaw) continue;

    try {
      const snapshot = JSON.parse(snapshotRaw);
      const snapshotDoctorId = String(
        snapshot?.doctor?.doctorId || snapshot?.doctor?.userId || "",
      ).trim();
      if (snapshotDoctorId === targetId) {
        return row;
      }
    } catch {
      // ignore malformed snapshot
    }
  }

  return null;
}

export const listPreCredentialApplications = async (
  accountId?: string,
): Promise<HospitalDoctorPreCredentialDTO[]> => {
  return withLegacyFallback(
    async () =>
      (
        await axios.get<ResponseData<HospitalDoctorPreCredentialDTO[]>>(
          `${PRE_CREDENTIAL_PRIMARY_BASE}/applications`,
          withAccountHeader(accountId),
        )
      ).data,
    async () =>
      (
        await axios.get<ResponseData<HospitalDoctorPreCredentialDTO[]>>(
          `${PRE_CREDENTIAL_LEGACY_BASE}/applications`,
          withAccountHeader(accountId),
        )
      ).data,
  );
};

export const getPreCredentialById = async (
  preCredentialId: string,
  accountId?: string,
): Promise<HospitalDoctorPreCredentialDTO> => {
  return withLegacyFallback(
    async () =>
      (
        await axios.get<ResponseData<HospitalDoctorPreCredentialDTO>>(
          `${PRE_CREDENTIAL_PRIMARY_BASE}/${preCredentialId}`,
          withAccountHeader(accountId),
        )
      ).data,
    async () =>
      (
        await axios.get<ResponseData<HospitalDoctorPreCredentialDTO>>(
          `${PRE_CREDENTIAL_LEGACY_BASE}/${preCredentialId}`,
          withAccountHeader(accountId),
        )
      ).data,
  );
};

export const revokePreCredential = async (
  preCredentialId: string,
  options?: { deleteRecord?: boolean },
  accountId?: string,
): Promise<HospitalDoctorPreCredentialDTO> => {
  const deleteRecord = Boolean(options?.deleteRecord);
  const suffix = `${preCredentialId}/revoke?deleteRecord=${deleteRecord}`;

  return withLegacyFallback(
    async () =>
      (
        await axios.post<ResponseData<HospitalDoctorPreCredentialDTO>>(
          `${PRE_CREDENTIAL_PRIMARY_BASE}/${suffix}`,
          undefined,
          withAccountHeader(accountId),
        )
      ).data,
    async () =>
      (
        await axios.post<ResponseData<HospitalDoctorPreCredentialDTO>>(
          `${PRE_CREDENTIAL_LEGACY_BASE}/${suffix}`,
          undefined,
          withAccountHeader(accountId),
        )
      ).data,
  );
};

export const deletePreCredential = async (
  preCredentialId: string,
  accountId?: string,
): Promise<HospitalDoctorPreCredentialDTO> => {
  return withLegacyFallback(
    async () =>
      (
        await axios.delete<ResponseData<HospitalDoctorPreCredentialDTO>>(
          `${PRE_CREDENTIAL_PRIMARY_BASE}/${preCredentialId}`,
          withAccountHeader(accountId),
        )
      ).data,
    async () =>
      (
        await axios.delete<ResponseData<HospitalDoctorPreCredentialDTO>>(
          `${PRE_CREDENTIAL_LEGACY_BASE}/${preCredentialId}`,
          withAccountHeader(accountId),
        )
      ).data,
  );
};

export const createPreCredentialDocRequest = async (
  preCredentialId: string,
  payload: HospitalPreCredentialDocRequestPayload,
  accountId?: string,
): Promise<HospitalPreCredentialRequestedFileDTO> => {
  return withLegacyFallback(
    async () =>
      (
        await axios.post<ResponseData<HospitalPreCredentialRequestedFileDTO>>(
          `${PRE_CREDENTIAL_PRIMARY_BASE}/${preCredentialId}/doc-requests`,
          payload,
          withAccountHeader(accountId),
        )
      ).data,
    async () =>
      (
        await axios.post<ResponseData<HospitalPreCredentialRequestedFileDTO>>(
          `${PRE_CREDENTIAL_LEGACY_BASE}/${preCredentialId}/doc-requests`,
          payload,
          withAccountHeader(accountId),
        )
      ).data,
  );
};

export const listPreCredentialDocRequests = async (
  preCredentialId: string,
  accountId?: string,
): Promise<HospitalPreCredentialRequestedFileDTO[]> => {
  return withLegacyFallback(
    async () =>
      (
        await axios.get<ResponseData<HospitalPreCredentialRequestedFileDTO[]>>(
          `${PRE_CREDENTIAL_PRIMARY_BASE}/${preCredentialId}/doc-requests`,
          {
            ...withAccountHeader(accountId),
            params: { _t: Date.now() },
          },
        )
      ).data,
    async () =>
      (
        await axios.get<ResponseData<HospitalPreCredentialRequestedFileDTO[]>>(
          `${PRE_CREDENTIAL_LEGACY_BASE}/${preCredentialId}/doc-requests`,
          {
            ...withAccountHeader(accountId),
            params: { _t: Date.now() },
          },
        )
      ).data,
  );
};
